import { ShareIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { InviteCollaboratorsModal } from '../InviteCollaboratorsModal';

export interface PageHeaderProps {
  /** The main title of the page */
  title: string;
  /** Optional subtitle or project name */
  subtitle?: string;
  /** Icon component to display in the colored container */
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Background color for the icon container (default: green) */
  iconBgColor?: string;
  /** Optional additional content below the title */
  children?: React.ReactNode;
  /** Show the built-in "Share" action (default true). Set false on pages that
   *  provide their own Share control (e.g. the Architecture Analysis page). */
  showShareAction?: boolean;
}

/**
 * PageHeader - Consistent header component for all stage pages
 * Provides unified styling with icon, title, subtitle, and optional content.
 *
 * When the current route is scoped to a project (a `:projectId` param is
 * present), a "Share" action is shown on the right so collaborators can be
 * invited from anywhere in the generation flow. It is automatically hidden on
 * the initial project-creation page (`/create`), which has no `:projectId`.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  icon: IconComponent,
  iconBgColor = '#008557', // Default green color from Review page
  children,
  showShareAction = true,
}) => {
  const { projectId } = useParams<{ projectId?: string }>();
  const [isShareOpen, setIsShareOpen] = useState(false);
  const numericProjectId = projectId ? Number.parseInt(projectId, 10) : Number.NaN;
  const canShare = showShareAction && Number.isFinite(numericProjectId);

  return (
    <div className="mb-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: iconBgColor }}
          >
            <IconComponent className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{title}</h1>
            {subtitle && (
              <p className="text-sm text-gray-600">{subtitle}</p>
            )}
          </div>
        </div>
        {canShare && (
          <button
            type="button"
            onClick={() => setIsShareOpen(true)}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#008557] hover:text-[#008557]"
            title="Share this project - invite collaborators"
          >
            <ShareIcon className="w-4 h-4" />
            Share
          </button>
        )}
      </div>
      {children}
      {canShare && isShareOpen && (
        <InviteCollaboratorsModal
          isOpen
          onClose={() => setIsShareOpen(false)}
          projectId={numericProjectId}
        />
      )}
    </div>
  );
};
